import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { post } from 'axios';

import ModalContainer from 'components/ModalContainer';

import FilledButton from 'components/Buttons/FilledButton';

import ErrorMessage from 'components/FormGroup/ErrorMessage';
import DateField from 'components/FormGroup/DateField';
import InputField from 'components/FormGroup/InputField';

import endpoints from 'services/endpoints';
import { formatDate } from 'utils/moment';
import { percentAllocation } from 'utils/masks';

import * as S from './styles';

const { updateAllocation } = endpoints;

function EditAllocatedProfessional({
  employeeData,
  isOpen,
  setIsOpen,
  renderToast,
  reload,
}) {
  const {
    _dt_inicio_alocacao: initialDate,
    _dt_final_alocacao: finalDate,
    _percentual: percent,
    idFuncionario,
  } = employeeData;

  const [allocationPercent, setAllocationPercent] = useState('');
  const [initialAllocationDate, setInitialAllocationDate] = useState(
    new Date(),
  );
  const [finalAllocationDate, setFinalAllocationDate] = useState(new Date());

  const [allocationErrors, setAllocationErrors] = useState([]);

  useEffect(() => {
    const employeePercent = percent ? percent.replace('.00', '') : '';

    setAllocationErrors([]);
    setAllocationPercent(employeePercent);
    setInitialAllocationDate(new Date(initialDate));
    setFinalAllocationDate(new Date(finalDate));
  }, [employeeData, finalDate, initialDate, percent]);

  const handleSubmit = () => {
    const { _id_alocacao: id } = employeeData;

    const data = {
      id_usr_up: idFuncionario,
      dt_inicio_alocacao: formatDate(initialAllocationDate, 'YYYY-MM-DD'),
      dt_final_alocacao: formatDate(finalAllocationDate, 'YYYY-MM-DD'),
      percentual: allocationPercent,
    };

    post(`${updateAllocation}/${id}`, data)
      .then(() =>
        renderToast('information', 'Informações atualizadas com sucesso', 8000),
      )
      .then(() => reload(prev => !prev))
      .then(() => setIsOpen(false))
      .catch(e => {
        const response = e.response.data;
        setAllocationErrors(response);
        return renderToast(
          'error',
          'Erro ao realizar alterações. Por favor, tente novamente',
          8000,
        );
      });
  };

  return (
    <>
      <ModalContainer
        headText="Altere as informações do funcionário"
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      >
        <S.Item>
          <span>Nome:</span>
          {employeeData._nome}
        </S.Item>
        <S.Item>
          <span>Cargo:</span>
          {employeeData._nome_funcao}
        </S.Item>
        <S.Item>
          <span>Nível de senioridade:</span>
          {employeeData._nivel_senioridade}
        </S.Item>
        <S.Form>
          <InputField
            labelText="Percentual de alocação"
            placeholderText="Defina o percentual"
            value={allocationPercent}
            name="allocationPercent"
            width={50}
            handleChange={e => {
              setAllocationPercent(percentAllocation(e.target.value));
            }}
          />

          <DateField
            selectedValue={initialAllocationDate}
            name="initialAllocationDate"
            placeholderText="Selecione uma data"
            onlyMonth={false}
            handleChange={e => setInitialAllocationDate(e)}
            labelText="Data de início da alocação"
          />

          <DateField
            selectedValue={finalAllocationDate}
            name="finalAllocationDate"
            onlyMonth={false}
            placeholderText="Selecione uma data"
            handleChange={e => setFinalAllocationDate(e)}
            labelText="Data final da alocação"
          />

          {Object.keys(allocationErrors).length > 0 && (
            <S.Errors>
              <ErrorMessage
                message={`Disponibilidade de alocação: ${
                  100 - allocationErrors.percentual
                }%`}
              />
              <ErrorMessage
                isBold={false}
                message={`Possibilidade para alocação de 100% após ${formatDate(
                  allocationErrors.dt_final_projeto_maior_deadline,
                  'DD/MM/YYYY',
                )} (desalocação no ${allocationErrors.projeto_maior_deadline})`}
              />
              <ErrorMessage
                isBold={false}
                message={`Projeto com maior percentual de alocação: ${allocationErrors.projeto_max_percentual} (${allocationErrors.percentual_max_projeto}%)`}
              />
            </S.Errors>
          )}
          <S.Buttons>
            <FilledButton
              buttonClass="cancel"
              text="Cancelar"
              handleClick={() => setIsOpen(false)}
            />
            <FilledButton
              buttonClass="confirm"
              text="Salvar alterações"
              handleClick={handleSubmit}
            />
          </S.Buttons>
        </S.Form>
      </ModalContainer>
    </>
  );
}

export default EditAllocatedProfessional;

EditAllocatedProfessional.defaultProps = {
  isOpen: true,
  setIsOpen: () => {},
};

EditAllocatedProfessional.propTypes = {
  employeeData: PropTypes.arrayOf(
    PropTypes.shape({
      _nome: PropTypes.string,
      _nome_funcao: PropTypes.string,
      _nivel_senioridade: PropTypes.string,
      _dt_inicio_alocacao: PropTypes.string,
      _dt_final_alocacao: PropTypes.string,
      _percentual: PropTypes.string,
    }),
  ).isRequired,
  isOpen: PropTypes.bool,
  renderToast: PropTypes.func.isRequired,
  setIsOpen: PropTypes.func,
  reload: PropTypes.func.isRequired,
};
